import React, { useState, useEffect } from 'react';
import { templateAPI, znsAPI } from '../services/api';
import { toast } from 'react-toastify';
import '../styles/SendZNS.css';

function SendZNS() {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [templateData, setTemplateData] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await templateAPI.getAll();
      if (response.data.success) {
        setTemplates(response.data.data);
      }
    } catch (error) {
      toast.error('Không thể tải danh sách template');
    }
  };

  const handleTemplateChange = (e) => {
    const templateId = parseInt(e.target.value);
    const template = templates.find(t => t.id === templateId);
    
    setSelectedTemplate(template);
    
    // Reset template data
    if (template) {
      const params = JSON.parse(template.parameters || '[]');
      const initialData = {};
      params.forEach(param => {
        initialData[param.name] = '';
      });
      setTemplateData(initialData);
    } else {
      setTemplateData({});
    }
  };

  const handleDataChange = (paramName, value) => {
    setTemplateData({
      ...templateData,
      [paramName]: value
    });
  };

  const handleSend = async (e) => {
    e.preventDefault();

    if (!selectedTemplate) {
      toast.error('Vui lòng chọn template');
      return;
    }

    if (!phoneNumber) {
      toast.error('Vui lòng nhập số điện thoại');
      return;
    }

    // Validate all parameters filled
    const params = JSON.parse(selectedTemplate.parameters || '[]');
    for (const param of params) {
      if (!templateData[param.name]) {
        toast.error(`Vui lòng nhập ${param.description || param.name}`);
        return;
      }
    }

    setLoading(true);

    try {
      const response = await znsAPI.send({
        template_id: selectedTemplate.id,
        phone_number: phoneNumber,
        template_data: templateData
      });

      if (response.data.success) {
        toast.success('Gửi ZNS thành công!');
        
        // Reset form
        setPhoneNumber('');
        setTemplateData({});
        const initialData = {};
        params.forEach(param => {
          initialData[param.name] = '';
        });
        setTemplateData(initialData);
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Gửi ZNS thất bại';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Preview content with filled data
  const getPreviewContent = () => {
    if (!selectedTemplate) return '';
    
    let content = selectedTemplate.content;
    Object.keys(templateData).forEach(key => {
      const value = templateData[key] || `<${key}>`;
      content = content.replace(new RegExp(`<${key}>`, 'g'), value);
    });
    
    return content;
  };

  return (
    <div className="send-zns-container">
      <div className="page-header">
        <h1>Gửi tin nhắn ZNS</h1>
        <p>Điền thông tin và gửi tin nhắn đến khách hàng</p>
      </div>

      <div className="send-zns-content">
        <div className="send-zns-form">
          <form onSubmit={handleSend}>
            <div className="form-group">
              <label>Chọn template *</label>
              <select onChange={handleTemplateChange} disabled={loading} required>
                <option value="">-- Chọn template --</option>
                {templates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.template_name} (ID: {template.template_id})
                  </option>
                ))}
              </select>
            </div>

            {selectedTemplate && (
              <>
                <div className="form-group">
                  <label>Số điện thoại người nhận *</label>
                  <input
                    type="text"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="VD: 0912345678 hoặc 84912345678"
                    disabled={loading}
                    required
                  />
                  <small>Nhập số điện thoại Việt Nam (bắt đầu bằng 0 hoặc 84)</small>
                </div>

                <div className="template-params">
                  <h3>Thông tin cần điền</h3>
                  {JSON.parse(selectedTemplate.parameters || '[]').map(param => (
                    <div key={param.name} className="form-group">
                      <label>{param.description || param.name} *</label>
                      <input
                        type="text"
                        value={templateData[param.name] || ''}
                        onChange={(e) => handleDataChange(param.name, e.target.value)}
                        placeholder={`Nhập ${param.description || param.name}`}
                        disabled={loading}
                        required
                      />
                    </div>
                  ))}
                </div>

                <div className="form-actions">
                  <button 
                    type="submit" 
                    className="btn-primary btn-send"
                    disabled={loading}
                  >
                    {loading ? 'Đang gửi...' : '📤 Gửi ZNS'}
                  </button>
                </div>
              </>
            )}
          </form>
        </div>

        {selectedTemplate && (
          <div className="preview-panel">
            <h3>Xem trước tin nhắn</h3>
            <div className="preview-box">
              <div className="preview-header">
                <img src="/logo-nagen.png" alt="NAGEN" className="preview-logo" />
                <span>NAGEN</span>
              </div>
              <div className="preview-content">
                {getPreviewContent().split('\n').map((line, index) => (
                  <p key={index}>{line || '\u00A0'}</p>
                ))}
              </div>
              <div className="preview-footer">
                <small>Giá: {selectedTemplate.price}đ/tin</small>
              </div>
            </div>

            <div className="template-info">
              <h4>Thông tin template</h4>
              <div className="info-item">
                <span>ID Template:</span>
                <strong>{selectedTemplate.template_id}</strong>
              </div>
              <div className="info-item">
                <span>Loại:</span>
                <strong>{selectedTemplate.template_type}</strong>
              </div>
              <div className="info-item">
                <span>Trạng thái:</span>
                <span className={`badge ${selectedTemplate.status}`}>
                  {selectedTemplate.status === 'active' ? 'Đang hoạt động' : 'Không hoạt động'}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SendZNS;
